/**
 * Session Storage Helper Utilities
 * Handles provider type and review year persistence using browser sessionStorage
 * Data persists for the duration of the page session (until tab/browser is closed)
 */

export const SESSION_KEYS = {
    PROVIDER_TYPE: 'app_provider_type',
    REVIEW_YEAR: 'app_review_year'
} as const;

/**
 * Set a value in sessionStorage
 * @param key - Storage key
 * @param value - Value to store
 */
export const setSessionItem = (key: string, value: string): void => {
    try {
        sessionStorage.setItem(key, value);
    } catch (error) {
        console.error('Error setting sessionStorage item:', error);
    }
};

/**
 * Get a value from sessionStorage
 * @param key - Storage key
 * @returns Stored value or null if not found
 */
export const getSessionItem = (key: string): string | null => {
    try {
        return sessionStorage.getItem(key);
    } catch (error) {
        console.error('Error getting sessionStorage item:', error);
        return null;
    }
};

/**
 * Remove a value from sessionStorage
 * @param key - Storage key
 */
export const removeSessionItem = (key: string): void => {
    try {
        sessionStorage.removeItem(key);
    } catch (error) {
        console.error('Error removing sessionStorage item:', error);
    }
};

/**
 * Save provider type to sessionStorage
 * @param providerType - Provider type code
 */
export const saveProviderTypeToSession = (providerType: string): void => {
    setSessionItem(SESSION_KEYS.PROVIDER_TYPE, providerType);
};

/**
 * Get provider type from sessionStorage
 * @returns Provider type code or null
 */
export const getProviderTypeFromSession = (): string | null => {
    return getSessionItem(SESSION_KEYS.PROVIDER_TYPE);
};

/**
 * Save review year to sessionStorage
 * @param reviewYear - Review year
 */
export const saveReviewYearToSession = (reviewYear: number): void => {
    setSessionItem(SESSION_KEYS.REVIEW_YEAR, reviewYear.toString());
};

/**
 * Get review year from sessionStorage
 * @returns Review year or null
 */
export const getReviewYearFromSession = (): number | null => {
    const year = getSessionItem(SESSION_KEYS.REVIEW_YEAR);
    return year ? parseInt(year, 10) : null;
};

/**
 * Clear all app-related session storage
 */
export const clearAppSession = (): void => {
    try {
        removeSessionItem(SESSION_KEYS.PROVIDER_TYPE);
        removeSessionItem(SESSION_KEYS.REVIEW_YEAR);
    } catch (error) {
        console.error('Error clearing app session:', error);
    }
};
